<template>
    <section class="internal-page">
        <internal-page-header
            eyebrow="Processos em acompanhamento"
            :title="title"
            description="Busque por empresa, CNPJ, número do processo ou comarca para consultar informações, atos processuais e documentos."
        />
        <div class="internal-content">
            <div class="process-directory__controls">
                <nav class="process-directory__tabs" aria-label="Natureza do processo">
                    <router-link
                        v-for="caseType in caseTypes"
                        :key="caseType.id"
                        :to="caseType.route"
                        >{{ caseType.plural }}</router-link
                    >
                </nav>
                <v-text-field
                    v-model="search"
                    label="Buscar processo"
                    placeholder="Empresa, CNPJ, número ou comarca"
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    outlined
                    dense
                    hide-details
                />
            </div>
            <div v-if="!loading && !error" class="process-directory__results" role="status">
                <span>
                    <strong>{{ filtered.length }}</strong>
                    {{ filtered.length === 1 ? 'processo encontrado' : 'processos encontrados' }}
                </span>
                <span v-if="pages > 1">Página {{ page }} de {{ pages }}</span>
            </div>
            <page-state
                v-if="loading || error || !filtered.length"
                :loading="loading"
                :error="error"
                inline
                title="Nenhum processo encontrado"
                description="Não há processos disponíveis para esta consulta. Tente outro nome, número ou comarca."
                @retry="load"
            />
            <ul v-else class="process-directory__list" aria-label="Processos encontrados">
                <li v-for="item in visible" :key="item.id">
                    <component
                        :is="hasExternalSite(item) ? 'a' : 'router-link'"
                        v-bind="caseLinkProps(item, `${routeBase}/${item.id}`)"
                        class="process-directory__row"
                        :aria-label="
                            hasExternalSite(item)
                                ? `Acessar o site do processo de ${item.name} (abre em nova aba)`
                                : `Consultar processo de ${item.name}`
                        "
                    >
                        <div class="process-directory__identity">
                            <div class="process-directory__logo" aria-hidden="true">
                                <img v-if="item.logo_url" :src="item.logo_url" alt="" />
                                <span v-else>{{ initials(item.name) }}</span>
                            </div>
                            <h2>{{ item.name }}</h2>
                        </div>
                        <dl class="process-directory__details">
                            <div>
                                <dt>Número do processo</dt>
                                <dd>{{ item.process_number || 'Não informado' }}</dd>
                            </div>
                            <div>
                                <dt>Comarca</dt>
                                <dd>
                                    {{ (item.district && item.district.name) || 'Não informada' }}
                                </dd>
                            </div>
                        </dl>
                        <div class="process-directory__action" aria-hidden="true">
                            <span>{{
                                hasExternalSite(item)
                                    ? 'Acessar site do caso'
                                    : 'Consultar processo'
                            }}</span>
                            <span class="process-directory__arrow"
                                ><v-icon>{{
                                    hasExternalSite(item)
                                        ? 'mdi-open-in-new'
                                        : 'mdi-arrow-top-right'
                                }}</v-icon></span
                            >
                        </div>
                    </component>
                </li>
            </ul>
            <v-pagination
                v-if="!loading && !error && pages > 1"
                v-model="page"
                :length="pages"
                :total-visible="5"
                class="mt-10"
            />
        </div>
    </section>
</template>
<script>
import axios from 'axios';
import caseTypes from '@/common/case-types.data';
import { caseLinkProps, hasExternalSite } from '@/common/util/case-link';
export default {
    name: 'ProcessDirectory',
    props: {
        type: { type: Number, required: true },
        title: { type: String, required: true },
        routeBase: { type: String, required: true }
    },
    data: () => ({
        caseTypes,
        enterprises: [],
        search: '',
        loading: true,
        error: false,
        page: 1,
        size: 12
    }),
    computed: {
        filtered() {
            const query = this.normalize(this.search);
            if (!query) return this.enterprises;

            const terms = query.split(' ');
            const numberOnly = /^[\d\s./\-–—]+$/.test(query);
            const queryDigits = query.replace(/\D/g, '');

            return this.enterprises.filter((item) => {
                const text = this.normalize(
                    `${item.name || ''} ${item.process_number || ''} ${
                        (item.district && item.district.name) || ''
                    }`
                );
                const processDigits = String(item.process_number || '').replace(/\D/g, '');
                const registrationDigits = String(item.registration || '').replace(/\D/g, '');
                const matchesDigits = (digits) =>
                    processDigits.includes(digits) || registrationDigits.includes(digits);

                // Compare the full number before splitting terms so CNJ/CNPJ separators never
                // turn a single number into independent, unrelated matches.
                if (numberOnly && queryDigits) {
                    return matchesDigits(queryDigits) || text.includes(query);
                }

                return terms.every((term) => {
                    const digits = term.replace(/\D/g, '');
                    return (
                        text.includes(term) ||
                        (/^[\d./\-–—]+$/.test(term) && digits && matchesDigits(digits))
                    );
                });
            });
        },
        pages() {
            return Math.ceil(this.filtered.length / this.size);
        },
        visible() {
            return this.filtered.slice((this.page - 1) * this.size, this.page * this.size);
        }
    },
    watch: {
        search() {
            this.page = 1;
        }
    },
    created() {
        this.load();
    },
    methods: {
        caseLinkProps,
        hasExternalSite,
        normalize(value) {
            return String(value == null ? '' : value)
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase()
                .trim()
                .replace(/\s+/g, ' ');
        },
        initials(name) {
            return (name || '')
                .split(' ')
                .filter(Boolean)
                .slice(0, 2)
                .map((word) => word[0])
                .join('');
        },
        async load() {
            this.loading = true;
            this.error = false;
            try {
                const response = await axios.get('/api/clients', {
                    params: { limit: 10000, search: JSON.stringify({ type: this.type }) }
                });
                const content = response.data.content;
                this.enterprises = Array.isArray(content) ? content : content.data || [];
            } catch (error) {
                this.error = true;
            } finally {
                this.loading = false;
            }
        }
    }
};
</script>
<style scoped>
.process-directory__controls {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 28px;
    padding: 20px;
    border: 1px solid #e1ddd5;
    border-radius: 8px;
    background: #fff;
}
.process-directory__controls > .v-input {
    flex: 1 1 340px;
    max-width: 440px;
    min-width: 0;
}
.process-directory__tabs {
    display: flex;
    flex-shrink: 0;
    gap: 4px;
    padding: 4px;
    border-radius: 6px;
    background: var(--pbb-cream);
}
.process-directory__tabs a {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    padding: 10px 18px;
    border-radius: 4px;
    color: var(--pbb-muted);
    text-decoration: none;
    font-size: 13px;
    font-weight: 600;
    transition: background 180ms ease, color 180ms ease;
}
.process-directory__tabs a:hover {
    color: var(--pbb-navy);
}
.process-directory__tabs a.router-link-active {
    color: #fff;
    background: var(--pbb-navy);
}
.process-directory__results {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 28px 0 18px;
    color: var(--pbb-muted);
    font-size: 12px;
}
.process-directory__results strong {
    color: var(--pbb-navy);
}
.process-directory__list {
    margin: 0;
    padding: 0;
    list-style: none;
    border-top: 1px solid #d9d5cc;
}
.process-directory__list > li {
    border-bottom: 1px solid #d9d5cc;
}
.process-directory__row {
    display: grid;
    grid-template-columns: minmax(0, 1.3fr) minmax(0, 1.2fr) auto;
    align-items: center;
    gap: 28px;
    min-width: 0;
    padding: 24px 16px;
    text-decoration: none;
    color: var(--pbb-navy);
    transition: background 200ms ease;
}
.process-directory__row:hover,
.process-directory__row:focus-visible {
    background: #fff;
}
.process-directory__row:focus-visible,
.process-directory__tabs a:focus-visible {
    outline: 2px solid var(--pbb-copper);
    outline-offset: 4px;
}
.process-directory__identity {
    display: flex;
    align-items: center;
    gap: 18px;
    min-width: 0;
}
.process-directory__logo {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 56px;
    height: 56px;
    border: 1px solid #e7e3dc;
    border-radius: 6px;
    background: #faf9f6;
    font: 500 20px/1 'Plus Jakarta Sans', sans-serif;
}
.process-directory__logo img {
    width: 80%;
    height: 80%;
    object-fit: contain;
}
.process-directory__identity h2 {
    margin: 0;
    font: 500 19px/1.4 'Plus Jakarta Sans', sans-serif;
    letter-spacing: -0.03em;
    overflow-wrap: anywhere;
}
.process-directory__details {
    display: grid;
    grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
    gap: 20px;
    margin: 0;
}
.process-directory__details dt {
    color: var(--pbb-muted);
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.07em;
    text-transform: uppercase;
}
.process-directory__details dd {
    margin: 7px 0 0;
    font-size: 13px;
    line-height: 1.6;
    font-variant-numeric: tabular-nums;
    overflow-wrap: anywhere;
}
.process-directory__action {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    font-size: 12px;
    font-weight: 600;
}
.process-directory__arrow {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    background: var(--pbb-cream);
    border-radius: 50%;
    transition: background 200ms ease;
}
.process-directory__arrow .v-icon {
    color: #a76536;
    font-size: 21px;
}
.process-directory__row:hover .process-directory__arrow,
.process-directory__row:focus-visible .process-directory__arrow {
    background: #f2dfcf;
}
.process-directory__controls + .page-state {
    margin-top: 24px;
}
@media (max-width: 1000px) {
    .process-directory__action > span:first-child {
        display: none;
    }
}
@media (max-width: 760px) {
    .process-directory__controls {
        flex-direction: column;
        align-items: stretch;
        gap: 20px;
        padding: 16px;
    }
    .process-directory__controls > .v-input {
        flex: auto;
        max-width: none;
    }
    .process-directory__tabs a {
        flex: 1;
        padding: 10px;
        font-size: 12px;
        text-align: center;
    }
    .process-directory__row {
        grid-template-columns: minmax(0, 1fr) 34px;
        gap: 16px 14px;
        padding: 22px 8px;
    }
    .process-directory__identity {
        gap: 14px;
    }
    .process-directory__identity h2 {
        font-size: 18px;
    }
    .process-directory__logo {
        width: 52px;
        height: 52px;
    }
    .process-directory__details {
        grid-column: 1 / -1;
        grid-row: 2;
        padding-left: 66px;
        gap: 16px;
    }
    .process-directory__action {
        grid-column: 2;
        grid-row: 1;
    }
}
@media (max-width: 480px) {
    .process-directory__details {
        grid-template-columns: minmax(0, 1fr);
    }
}
@media (prefers-reduced-motion: reduce) {
    .process-directory__row,
    .process-directory__arrow,
    .process-directory__tabs a {
        transition: none;
    }
}
</style>
